import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DateValueType } from "../../DatepickerV2";
import type {
  AssetGroup,
  Hoid,
  HoidPartner,
  ProgramDetailPatch,
  ProgramDetailPatchState,
} from "./data";

import { Button as ButtonV2 } from "../../Button";
import { CopyButton } from "../../CopyButton";
import Datepicker from "../../DatepickerV2";
import { getShortcutButtonLabel } from "../../DatepickerV2/helpers/shortcutLabel";
import { Drawer } from "../../Drawer";
import { useDatepickerTranslations } from "../../hooks/useDatepickerTranslations";
import {
  BaseArchiveIcon,
  BaseArrowDownIcon,
  BaseInfoIcon,
  ChevronLeftIcon,
  CrossIcon,
  ErrorIconV2,
} from "../../Icons";
import { InlineNotification } from "../../InlineNotification";
import { SkeletonLoader } from "../../SkeletonLoader";
import { StatusPill } from "../../StatusPill";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import {
  ALL_TIME_START,
  getAllTimeDateValue,
} from "../providers/getDefaultDateRange";
import {
  fmtDate,
  fmtInt,
  fmtVolExact,
  fmtVolMln,
  hasUndismissedRejection,
  isRecentlyAdded,
} from "./data";
import { RebateSplitOverrideSection } from "./RebateSplitOverrideSection";
import { SubIbStatusTag } from "./SubIbStatusTag";

type ProgramDetailsDrawerProps = {
  hoid: Hoid | null;
  onClose: () => void;
  onArchive: (hoidId: string) => void;
  /**
   * Present when this drawer replaced the Sub IB drawer — opened from a "View
   * program detail" link in its Rebate programs list. Renders the leading back
   * affordance (canvas `ProgramDrawer`'s `drawer__back`, spec §7.3
   * "replace + back"); omitted when opened straight from the table.
   */
  onBack?: () => void;
  /**
   * Fetches this program's detail (GET rebate program detail). The **fetcher**,
   * not its state: the drawer owns the call so its loading and error states are
   * its own, and Refresh re-runs it. Keyed on the drawer's own Performance date
   * chip, so changing that refetches — which it did not when the view owned this.
   *
   * Omitted in mock/Storybook/tests, where `hoid`'s own fields are already
   * real synchronous numbers and there is nothing to load.
   */
  fetchDetail?: (
    programId: string,
    dateRange: { from: Date | null; to: Date | null },
  ) => Promise<ProgramDetailPatch>;
  /**
   * The page's own date filter. One-way sync: seeds the drawer's Performance
   * date chip on open, and pulls it back in line whenever the page filter
   * changes while the drawer is already open — but the chip's own picker
   * (below) only ever updates local state and never writes back to this.
   * Omitted in mock/Storybook/tests, where the chip just keeps its
   * last-30-days default.
   */
  pageDateRange?: { from: Date | null; to: Date | null };
  assets?: AssetGroup[] | null;
  showSubIbStatus?: boolean;
  onDismissRejectedNotification?: (partner: HoidPartner) => Promise<void>;
  /** Phase 2: closed-program banner gets the new copy, style and placement. */
  phase2Enabled?: boolean;
};

/**
 * Scoped to just one field — a date-chip-triggered reload keeps the rest of
 * the drawer (Summary, the other Performance fields, Rebate split) as-is;
 * only the field(s) actually reloading show this. SkeletonLoader doesn't
 * forward arbitrary props, hence the wrapper span for a stable query target
 * in tests.
 */
const PerformanceFieldSkeleton = () => (
  <span data-testid="performance-field-loading">
    <SkeletonLoader height={20} width={72} variant="rounded" />
  </span>
);

const renderMoneyField = (
  fallback: number,
  patchState: ProgramDetailPatchState | undefined,
  pick: (patch: ProgramDetailPatch) => number,
): React.ReactNode => {
  const resolved = !patchState
    ? fallback
    : patchState.status === "success"
      ? pick(patchState.value)
      : null;
  if (resolved === null) {
    return patchState?.status === "loading" ? (
      <PerformanceFieldSkeleton />
    ) : (
      <Typo element="span" type="bodyMD" textColor="text-text-disabled">
        --
      </Typo>
    );
  }
  const formatted = fmtVolMln(resolved);
  if (Math.abs(resolved) < 1e6) return formatted;
  return (
    <Typo element="span" type="bodyMD" className="break-words">
      <TooltipWrapper
        text={fmtVolExact(resolved)}
        color="blue"
        positionStrategy="fixed"
        tooltipPlacement="top"
        tooltipMaxWidth={240}
        tooltipContainerClassName="!text-xs"
      >
        <span>{formatted}</span>
      </TooltipWrapper>
    </Typo>
  );
};

const KV = ({
  label,
  value,
  info,
  valueColor,
}: {
  label: string;
  value: React.ReactNode;
  info?: string;
  /**
   * Overrides the value's colour. Used to dim an absent date's `--` to
   * `text-text-disabled`, per the canvas's `.dash` rule.
   */
  valueColor?: string;
}) => (
  <div className="flex min-w-0 flex-col gap-2">
    <span className="inline-flex items-center gap-1">
      <Typo
        element="span"
        type="labelXS"
        textColor="text-text-tertiary"
        className="uppercase tracking-[0.04em]"
      >
        {label}
      </Typo>
      {info && (
        <TooltipWrapper
          text={info}
          color="blue"
          // Opens upward like the table headers and the canvas's `.tip__pop`.
          // No `positionStrategy="fixed"` here: the drawer panel is a
          // containing block for fixed elements (`translate-x-0`) and clips with
          // `overflow-hidden`, so `fixed` would buy nothing and only risk
          // mispositioning. `flip()` falls back to bottom for a field sitting at
          // the very top of the scrolled body.
          tooltipPlacement="top"
          tooltipMaxWidth={240}
          tooltipContainerClassName="!text-xs"
        >
          <BaseInfoIcon
            height={16}
            width={16}
            className="shrink-0 text-icon-tertiary"
            aria-hidden="true"
          />
        </TooltipWrapper>
      )}
    </span>
    {typeof value === "string" ? (
      <Typo
        element="span"
        type="bodyMD"
        textColor={valueColor}
        className="break-words"
      >
        {value}
      </Typo>
    ) : (
      value
    )}
  </div>
);

/**
 * The Rebate split table's Payout cell — same string-vs-node branch as `KV`,
 * so a loading skeleton renders as a plain node instead of nested inside the
 * `Typo` span meant for text.
 */
const PayoutValue = ({ value }: { value: React.ReactNode }) =>
  typeof value === "string" ? (
    <Typo element="span" type="bodyMD" className="text-right">
      {value}
    </Typo>
  ) : (
    <span className="flex justify-end">{value}</span>
  );

const DateRangeChip = ({
  value,
  onChange,
  onOpenChange,
}: {
  value: DateValueType;
  onChange: (v: DateValueType) => void;
  /** PART-4256: lets the drawer lock its own scrolling while the calendar is open. */
  onOpenChange?: (isOpen: boolean) => void;
}) => {
  const { t } = useTranslation();
  // Stable for the chip's lifetime — anchors the calendar's empty-value panel
  // months (see `calendarStartFrom` below) without retriggering
  // `DatepickerV2`'s value-sync effect on every unrelated re-render.
  const [todayAnchor] = useState(() => new Date());
  const baseConfigs = useDatepickerTranslations();
  // Mirrors the toolbar's own `datepickerConfigs` (SharedRebateProgramsView) —
  // `allTime` must be present here too, or a value that's actually the
  // all-time range (the drawer's own default) fails to collapse and falls
  // through to two raw formatted dates instead of "All time".
  const configs = useMemo(
    () => ({
      ...baseConfigs,
      shortcuts: {
        allTime: {
          text: t("subIBReport_dateAllTime", "All time"),
          period: {
            start: ALL_TIME_START,
            end: (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return today;
            })(),
          },
        },
        ...baseConfigs.shortcuts,
      },
    }),
    [baseConfigs, t],
  );
  // With a real seeded value the trigger would otherwise show two formatted
  // dates; this collapses an exact preset match back to its shortcut name.
  const shortcutLabel = getShortcutButtonLabel(value, configs);
  const allTimeLabel = t("subIBReport_dateAllTime", "All time");
  // "All time" isn't a bounded range — showing it as a highlighted 2021-to-
  // today selection when the calendar opens misrepresents it as one. Feed
  // the calendar an empty value instead so it opens on today/next month with
  // nothing highlighted; the trigger label above is computed from the real
  // `value`, so it still reads "All time" regardless.
  const calendarValue =
    shortcutLabel === allTimeLabel ? { startDate: null, endDate: null } : value;
  // `DatepickerV2`'s own empty-value fallback opens on *previous*/current
  // month, one month off the design ref's current/next — `startFrom`
  // corrects that, scoped to only the nulled-out "All time" case so a real
  // same-month range's second panel is unaffected.
  const calendarStartFrom =
    shortcutLabel === allTimeLabel ? todayAnchor : undefined;

  return (
    <Datepicker
      value={calendarValue}
      startFrom={calendarStartFrom}
      onChange={onChange}
      maxDate={todayAnchor}
      useRange
      showShortcuts
      showFooter
      configs={configs}
      displayFormat="DD MMM YYYY"
      separator=" - "
      positionStrategy="fixed"
      onOpenChange={onOpenChange}
      trigger={({
        open,
        inputText,
        isEmpty,
      }: {
        open: () => void;
        inputText: string;
        isEmpty: boolean;
      }) => (
        <button
          type="button"
          onClick={open}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-stroke-primary px-2 hover:bg-surface-tonal-default"
        >
          <Typo element="span" type="labelXS" textColor="text-text-primary">
            {shortcutLabel ??
              (isEmpty ? t("subIBReport_dateAllTime", "All time") : inputText)}
          </Typo>
          <BaseArrowDownIcon className="h-4 w-4 text-text-primary" />
        </button>
      )}
    />
  );
};

const DrawerErrorBody = ({ onRetry }: { onRetry?: () => void }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-6 text-center"
      role="alert"
      data-testid="program-drawer-error"
    >
      <ErrorIconV2 width={64} height={64} aria-hidden="true" />
      <div className="flex flex-col items-center gap-2">
        <Typo element="span" type="labelMD" textColor="text-text-primary">
          {t("subIBReport_detailErrorTitle", "Unable to load details")}
        </Typo>
        <Typo element="span" type="bodySM" textColor="text-text-secondary">
          {t(
            "subIBReport_detailErrorDescription",
            "Something went wrong. Refresh to try again.",
          )}
        </Typo>
      </div>
      {onRetry && (
        <ButtonV2 variant="brand" size="md" onClick={onRetry}>
          {t("common_refresh", "Refresh")}
        </ButtonV2>
      )}
    </div>
  );
};

/**
 * One `KV` placeholder: label and value are separate bars with the same `gap-2`
 * `KV` itself uses, so the two lines land where the real label/value will.
 * A single merged bar per field reads as one wide value and shifts the layout
 * on load.
 */
const KVSkeleton = () => (
  <div className="flex min-w-0 flex-col gap-2">
    <SkeletonLoader height={12} width={88} variant="rounded" />
    <SkeletonLoader height={20} width={112} variant="rounded" />
  </div>
);

/**
 * Loading body, mirroring the canvas's `DrawerStateBody` `program` variant:
 * summary (name + status pill · referral link · 3-col info) · performance grid
 * (6 fields, 3 columns) · rebate split (heading + bar block).
 *
 * Summary and Performance track the loaded body one-for-one — same section gaps
 * (`gap-8` outer, `gap-6` summary, `gap-4` performance), same `grid-cols-3
 * gap-6` field grids, and heights matching the real controls (`h-5` status
 * pill, `h-10` referral-link pill, `h-8` date chip) — so nothing jumps when the
 * real content swaps in. Rebate split stays a deliberately coarse
 * heading-plus-block placeholder: its real row count is data-dependent (one per
 * Sub IB), so a row-for-row mirror would only be right by accident.
 */
const DrawerSkeletonBody = () => (
  <div
    className="flex flex-col gap-8"
    aria-busy="true"
    data-testid="program-drawer-loading"
  >
    {/* Summary */}
    <section className="flex flex-col gap-6">
      {/* Program label (HOID). The status pill it sits next to when loaded has
          no placeholder on purpose — it resolves with the row rather than
          streaming in, so a second bar here reads as content that never
          arrives. */}
      <SkeletonLoader height={20} width={180} variant="rounded" />
      {/* "Referral link" label + the link pill */}
      <div className="flex flex-col gap-2">
        <SkeletonLoader height={12} width={88} variant="rounded" />
        <SkeletonLoader height={40} variant="rounded" />
      </div>
      <div className="grid grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <KVSkeleton key={i} />
        ))}
      </div>
    </section>

    {/* Performance */}
    <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
      {/* Heading + date-range chip */}
      <div className="flex items-center justify-between gap-4">
        <SkeletonLoader height={16} width={120} variant="rounded" />
        <SkeletonLoader height={32} width={128} radius="full" />
      </div>
      <div className="grid grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <KVSkeleton key={i} />
        ))}
      </div>
    </section>

    {/* Rebate split */}
    <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
      <SkeletonLoader height={18} width={100} variant="rounded" />
      <SkeletonLoader height={56} variant="rounded" />
    </section>
  </div>
);

const SPLIT_COLORS = [
  "bg-surface-brand-default",
  "bg-surface-emphasis",
  "bg-surface-accent-blue-subtler",
];

export const ProgramDetailsDrawer = ({
  hoid,
  onClose,
  onArchive,
  onBack,
  fetchDetail,
  pageDateRange,
  assets,
  showSubIbStatus,
  onDismissRejectedNotification,
  phase2Enabled,
}: ProgramDetailsDrawerProps) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateValueType>(() =>
    pageDateRange
      ? { startDate: pageDateRange.from, endDate: pageDateRange.to }
      : getAllTimeDateValue(),
  );
  const pageFromTime = pageDateRange?.from?.getTime() ?? null;
  const pageToTime = pageDateRange?.to?.getTime() ?? null;
  useEffect(() => {
    if (!pageDateRange) return;
    setDateRange((prev) => {
      const prevFromTime = prev?.startDate?.getTime() ?? null;
      const prevToTime = prev?.endDate?.getTime() ?? null;
      // Bail out (same reference) once the chip already matches the page
      // filter — otherwise this would hand the fetch effect below a
      // referentially-new (but value-equal) dateRange on every render,
      // firing a redundant refetch.
      if (prevFromTime === pageFromTime && prevToTime === pageToTime) {
        return prev;
      }
      return { startDate: pageDateRange.from, endDate: pageDateRange.to };
    });
    // pageDateRange itself is deliberately omitted: its identity can change
    // on every parent render even when from/to don't, and the primitive
    // timestamps are what should actually gate this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFromTime, pageToTime]);
  // Seeded to "loading" up front (not left `undefined` until the fetch
  // effect below runs) so the very first paint already shows the full
  // skeleton — otherwise this briefly renders `hoid`'s stale, page-scoped
  // numbers via `renderMoneyField`'s `!patchState` fallback, then
  // flashes to the skeleton once the effect fires post-paint.
  const [detail, setDetail] = useState<ProgramDetailPatchState | undefined>(
    () => (fetchDetail ? { status: "loading" } : undefined),
  );
  // Bumped by Refresh to re-run the fetch effect without changing its inputs.
  const [retryCount, setRetryCount] = useState(0);
  const [isDateChipOpen, setIsDateChipOpen] = useState(false);
  // Gates the full-drawer skeleton/error to the first load only. Once the
  // drawer has shown real content at least once, a later refetch (the date
  // chip changing) must not blank Summary/etc. again — only the fields that
  // are actually reloading (Performance's 4 date-scoped fields, and the
  // Rebate split section's Payout column) should show it.
  const hasLoadedOnceRef = useRef(false);

  const programApiId = hoid ? (hoid.programId ?? hoid.id) : null;
  const rangeFrom = dateRange?.startDate ?? null;
  const rangeTo = dateRange?.endDate ?? null;
  // Primitive keys so the effect doesn't re-run on a new Date object or a new
  // fetcher identity.
  const fromKey = rangeFrom instanceof Date ? rangeFrom.getTime() : null;
  const toKey = rangeTo instanceof Date ? rangeTo.getTime() : null;

  useEffect(() => {
    if (!programApiId || !fetchDetail) {
      setDetail(undefined);
      return;
    }
    let alive = true;
    setDetail({ status: "loading" });
    fetchDetail(programApiId, {
      from: fromKey === null ? null : new Date(fromKey),
      to: toKey === null ? null : new Date(toKey),
    })
      .then((patch) => {
        if (alive) {
          hasLoadedOnceRef.current = true;
          setDetail({ status: "success", value: patch });
        }
      })
      .catch(() => {
        if (alive) setDetail({ status: "error" });
      });
    return () => {
      alive = false;
    };
  }, [programApiId, fromKey, toKey, retryCount, fetchDetail]);

  if (!hoid) return null;

  return (
    <Drawer
      open
      onClose={onClose}
      ariaLabel={t("subIBReport_programDetailsTitle", "Rebate program details")}
    >
      <Drawer.Header className="border-b border-stroke-primary px-8 py-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label={t("common_back", "Back")}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-surface-secondary"
              >
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            <Typo element="h2" type="headingLG">
              {t("subIBReport_programDetailsTitle", "Rebate program details")}
            </Typo>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common_close", "Close")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-surface-secondary"
          >
            <CrossIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </Drawer.Header>

      <Drawer.Body scrollable={!isDateChipOpen} className="flex-col px-8 py-8">
        {!hasLoadedOnceRef.current && detail?.status === "error" ? (
          <DrawerErrorBody onRetry={() => setRetryCount((n) => n + 1)} />
        ) : !hasLoadedOnceRef.current && detail?.status === "loading" ? (
          <DrawerSkeletonBody />
        ) : (
          <div className="flex flex-col gap-8">
            {/* Summary */}
            <section className="flex flex-col gap-6">
              <div className="flex items-center gap-2">
                <Typo element="span" type="labelLG">
                  {hoid.rebateProgramLabel}
                </Typo>
                {isRecentlyAdded(hoid.createdAt) && (
                  <StatusPill
                    status="New"
                    label={t("subIBReport_newTag", "New")}
                  />
                )}
                <StatusPill
                  status={hoid.status}
                  label={
                    hoid.status === "Archived"
                      ? t("subIBReport_statusArchived", "Closed")
                      : undefined
                  }
                />
              </div>
              {hoid.status === "Archived" && phase2Enabled && (
                <InlineNotification variant="tonal" icon={BaseArchiveIcon}>
                  {t(
                    "subIBReport_archivedLinkExplainerPhase2",
                    "You still share rebates with Sub IBs on clients who joined before this program closed. Rebates from clients who join after the closure are 100% yours.",
                  )}
                </InlineNotification>
              )}
              <div className="flex flex-col gap-2">
                <Typo
                  element="span"
                  type="labelXS"
                  textColor="text-text-tertiary"
                  className="uppercase tracking-[0.04em]"
                >
                  {t("subIBReport_referralLink", "Referral link")}
                </Typo>
                {/* Canvas `.hcard__link-input` — "the whole pill is the field; the
                  URL fills its left side and the Copy button sits on the right".
                  `flex-1 min-w-0` on the URL is what pushes the button to the
                  right edge; without it the button sat immediately after the
                  text (`.hcard__link-url { flex: 1; min-width: 0 }`). */}
                {/* Canvas `.hcard__link-url`: "Long links wrap to a new
                  line rather than truncating" — min-h-10/py-2 (was h-10/py-0)
                  let the pill grow to fit the wrapped text. */}
                <div className="flex min-h-10 items-center gap-2 rounded-lg bg-surface-tonal-default py-2 pl-3.5 pr-1">
                  <Typo
                    element="span"
                    type="bodySM"
                    className="min-w-0 flex-1 break-words"
                  >
                    {hoid.referralLink}
                  </Typo>
                  <CopyButton
                    text={hoid.referralLink}
                    copyLabel={t(
                      "subIBReport_copyReferralLink",
                      "Copy referral link",
                    )}
                    copiedLabel={t("subIBReport_copied", "Copied")}
                    // The green-check swap is the whole confirmation here, per the
                    // canvas's `CopyLinkButton` — no tooltip on top of it.
                    showCopiedTooltip={false}
                    // `.copylink-icon` is 32px; CopyButton's desktop default is
                    // 24px and hardcodes it ahead of `className`, so `!` is needed
                    // to win (see packages/ui/CLAUDE.md). Resizing the shared
                    // default would move ~15 other consumers.
                    className="!h-8 !w-8"
                  />
                </div>
                {hoid.status === "Archived" && !phase2Enabled && (
                  <InlineNotification variant="neutral" icon={BaseArchiveIcon}>
                    {t(
                      "subIBReport_archivedLinkExplainer",
                      "Clients who joined before this program closed continue to share rebates with your sub IBs. Rebates from clients who join after are 100% yours.",
                    )}
                  </InlineNotification>
                )}
              </div>
              <div className="grid grid-cols-3 gap-6">
                <KV
                  label={t("subIBReport_programCreated", "Program created")}
                  value={fmtDate(hoid.createdAt)}
                  valueColor={hoid.createdAt ? undefined : "text-text-disabled"}
                />
                <KV
                  label={t("subIBReport_colLastPayment", "Last payment")}
                  value={fmtDate(hoid.lastPayment)}
                  // Absent date → the `--` dims, per the canvas's `.dash`.
                  valueColor={
                    hoid.lastPayment ? undefined : "text-text-disabled"
                  }
                  info={t(
                    "subIBReport_colLastPaymentInfoDrawer",
                    "Most recent payout date for this rebate program. Not affected by the selected date range.",
                  )}
                />
                <KV
                  label={t(
                    "subIBReport_yourRebateAccount",
                    "Your rebate account",
                  )}
                  value={hoid.accountNumber}
                  info={t(
                    "subIBReport_programRebateAccountInfo",
                    "The account where this program's rebate earnings are paid to",
                  )}
                />
              </div>
            </section>

            {/* Performance */}
            <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
              <div className="flex items-center justify-between gap-4">
                <Typo element="h3" type="labelMD">
                  {t("subIBReport_performance", "Performance")}
                </Typo>
                {/* DatepickerV2's own root wrapper renders with `w-full`, which as
                  a direct flex child claims the whole row — leaving
                  `justify-between` no free space and stranding the chip next to
                  the title instead of flush right. This wrapper absorbs the
                  `w-full` so the chip sizes to its content. Same fix as the Sub
                  IB drawer's aggregated-metrics header. */}
                <div className="shrink-0">
                  <DateRangeChip
                    value={dateRange}
                    onChange={setDateRange}
                    onOpenChange={setIsDateChipOpen}
                  />
                </div>
              </div>
              {/* A reload past the first load failing (date chip changed, the
                call rejected) must not blank the drawer — see hasLoadedOnceRef
                above. Refresh re-runs the same fetch effect via retryCount,
                same mechanism the full-drawer error uses. */}
              {detail?.status === "error" && hasLoadedOnceRef.current && (
                <InlineNotification
                  variant="error"
                  action={
                    <button
                      type="button"
                      onClick={() => setRetryCount((n) => n + 1)}
                      className="underline underline-offset-2 hover:no-underline"
                    >
                      <Typo
                        element="span"
                        type="bodySM"
                        textColor="text-text-primary"
                      >
                        {t("common_refresh", "Refresh")}
                      </Typo>
                    </button>
                  }
                >
                  {t(
                    "subIBReport_performanceReloadError",
                    "Something went wrong. Refresh to try again.",
                  )}
                </InlineNotification>
              )}
              <div className="grid grid-cols-3 gap-6">
                <KV
                  label={t("subIBReport_colTotalEarning", "Total earnings")}
                  value={renderMoneyField(
                    hoid.totalRebate,
                    detail,
                    (p) => p.totalRebate,
                  )}
                  info={t(
                    "subIBReport_colTotalEarningInfoDrawer",
                    "The total gross rebate earned across all programs shared between both you and your Sub IBs",
                  )}
                />
                <KV
                  label={t("subIBReport_colYourPayout", "Your payout")}
                  value={renderMoneyField(
                    hoid.myPayout,
                    detail,
                    (p) => p.myPayout,
                  )}
                />
                <KV
                  label={t("subIBReport_subIbPayout", "Sub IB payout")}
                  value={renderMoneyField(
                    hoid.subIbPayout,
                    detail,
                    (p) => p.subIbPayout,
                  )}
                />
                <KV
                  label={t("subIBReport_uniqueClients", "Clients")}
                  value={fmtInt(hoid.uniqueClients)}
                  info={t(
                    "subIBReport_colClientsInfo",
                    "All-time client data under this rebate program, regardless of the selected date range",
                  )}
                />
                <KV
                  label={t("subIBReport_clientAccounts", "Client accounts")}
                  value={fmtInt(hoid.clientAccounts)}
                  info={t(
                    "subIBReport_colClientsInfo",
                    "All-time client data under this rebate program, regardless of the selected date range",
                  )}
                />
                <KV
                  label={t("subIBReport_tradingVolume", "Trading volume")}
                  value={renderMoneyField(
                    hoid.tradingVolume,
                    detail,
                    (p) => p.tradingVolume,
                  )}
                />
              </div>
            </section>

            {/* Rebate split */}
            <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
              <Typo element="h3" type="labelMD">
                {t("subIBReport_colSplit", "Rebate split")}
              </Typo>
              <div className="flex h-4 gap-0.5 overflow-hidden rounded-full">
                <div
                  className={SPLIT_COLORS[0]}
                  style={{
                    width: `${hoid.masterSplitPct}%`,
                    minWidth: "4px",
                  }}
                />
                {hoid.partners.map((p, i) => (
                  <div
                    key={p.subIbId}
                    className={SPLIT_COLORS[(i + 1) % SPLIT_COLORS.length]}
                    style={{
                      width: `${p.splitPct}%`,
                      minWidth: "4px",
                    }}
                  />
                ))}
              </div>
              <div className="flex flex-col gap-4">
                <div
                  className="-mb-2 grid items-center gap-4"
                  style={{ gridTemplateColumns: "1fr 80px 140px" }}
                >
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-secondary"
                  >
                    {t("subIBReport_ibColumn", "IB")}
                  </Typo>
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-secondary"
                  >
                    {t("subIBReport_splitColumn", "Split %")}
                  </Typo>
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-secondary"
                    className="text-right"
                  >
                    {t("subIBReport_payoutColumn", "Payout")}
                  </Typo>
                </div>
                <div
                  className="grid items-center gap-4"
                  style={{ gridTemplateColumns: "1fr 80px 140px" }}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span
                      className={`h-4 w-4 shrink-0 rounded ${SPLIT_COLORS[0]}`}
                    />
                    <Typo element="span" type="bodyMD">
                      {t("subIBReport_you", "You")}
                    </Typo>
                  </span>
                  <Typo element="span" type="labelMD">
                    {`${hoid.masterSplitPct}%`}
                  </Typo>
                  <PayoutValue
                    value={renderMoneyField(
                      hoid.myPayout,
                      detail,
                      (p) => p.myPayout,
                    )}
                  />
                </div>
                {hoid.partners.map((p, i) => (
                  <div
                    key={p.subIbId}
                    className="grid items-center gap-4"
                    style={{ gridTemplateColumns: "1fr 80px 140px" }}
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className={`h-4 w-4 shrink-0 rounded ${SPLIT_COLORS[(i + 1) % SPLIT_COLORS.length]}`}
                      />
                      {/* Plain text, matching the "You" row above. Opening the Sub
                        IB drawer from here is deferred to phase 2. A no-op
                        button is worse than none: it keeps link styling and an
                        aria-label announcing an action that does not exist. */}
                      <Typo
                        element="span"
                        type="bodyMD"
                        className="line-clamp-2 min-w-0 break-words"
                      >
                        {p.name}
                      </Typo>
                      {showSubIbStatus && (
                        <div className="shrink-0">
                          <SubIbStatusTag
                            status={p.ibStatus}
                            hasRejectedNotification={hasUndismissedRejection(p)}
                            onRejectedNotificationSeen={() => {
                              void onDismissRejectedNotification?.(p);
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <Typo element="span" type="labelMD">
                      {`${p.splitPct}%`}
                    </Typo>
                    <PayoutValue
                      value={renderMoneyField(
                        p.payout,
                        detail,
                        (patch) => patch.subIbPayoutById[p.subIbId] ?? p.payout,
                      )}
                    />
                  </div>
                ))}
              </div>
            </section>

            <RebateSplitOverrideSection hoid={hoid} assets={assets} />

            {hoid.status !== "Archived" && (
              <section
                aria-label={t(
                  "subIBReport_archiveProgram",
                  "Close rebate program",
                )}
                className="flex flex-col border-t border-stroke-primary pt-8"
              >
                <ButtonV2
                  variant="negative"
                  size="md"
                  onClick={() => onArchive(hoid.id)}
                >
                  <div className="flex items-center gap-2">
                    <BaseArchiveIcon className="h-4 w-4" aria-hidden="true" />
                    {t(
                      "subIBReport_archiveThisProgram",
                      "Close this rebate program",
                    )}
                  </div>
                </ButtonV2>
              </section>
            )}
          </div>
        )}
      </Drawer.Body>
    </Drawer>
  );
};
